import React, { useState, useEffect, useRef } from 'react';
import { Filter, ChevronDown, ChevronUp, X, CircleUserRound, Search, Clock, MapPinned, LocateIcon, ChevronRight, Bookmark, BookmarkCheck, ChevronLeft } from 'lucide-react';
import { useMapStore } from '../store/mapStore';
import Filters from './Filters';
import { attemptGeo, defaultCenter } from '../utils/geo';



const MapControls = ({ showSearchHereTag, mapCenter, deviceLocation, setDeviceLocation, isOffFromDeviceLocation, setIsOffFromDeviceLocation, setShowSearchHereTag, map }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [showDesktopFilters, setShowDesktopFilters] = useState(false);

  useEffect(() => {
    console.log('window width:', window.innerWidth);
  }, []);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 800);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    filters,
    setFilters,
    searchRadius,
    setSearchRadius,
    recentlyViewed,
    setSelectedLocation,
    searchCenter,
    locations,
    searchHistory,
    applySearchHistory,
    clearFilters,
    currentSearchTerm,
    searchByLatLng,
    searchByZipCode,
    searchByAccountId,
    searchByAccountName,
    setGlobalSearchMode,
    setGlobalDeviceLocation,
    isFavorite,
    toggleFavorite,
    serverFavoritesRaw,
    setSearchVisible,
    getLocationId,
    controlsOpen,
    showBookmarksOnly,
    setShowBookmarksOnly
  } = useMapStore();

  const [expandedSections, setExpandedSections] = useState({
    search: true,
    distance: true,
    recent: true,
    suggestion: false,
    history: true
  });

  const toggleSection = (key) =>
    setExpandedSections((s) => ({ ...s, [key]: !s[key] }));


  const [searchMode, setSearchMode] = useState('location'); // 'location' or 'account'
  const [searchQuery, setSearchQuery] = useState('');
  //const { setGlobalSearchMode } = useMapStore.getState();
  const [openSaved, setOpenSaved] = useState(true);


  const [activeTab, setActiveTab] = useState('filters');

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUnifiedSearch(); // don't pass `e`
    }
  };

  const favoriteCards = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    const raw = Array.isArray(serverFavoritesRaw) ? serverFavoritesRaw : [];

    for (const f of raw) {
      const idObj = getLocationId(f);
      const key = idObj?.id;
      if (!key || seen.has(key)) continue;     // ← de-dupe by stable key
      seen.add(key);

      out.push({
        key,
        name: f.accountName || f.name || 'Unnamed',
        address: f.address || '',
        lat: f.latitude ?? null,
        lng: f.longitude ?? null,
        status: (f.accountStatus || f.status || '').toUpperCase(),
        scope: f.accountType || f.scope || '',
        accountType: f.accountType || f.scope || '',
      });
    }

    // optional: keep order stable; or sort by name if you prefer
    return out;
  }, [serverFavoritesRaw, getLocationId]);



  const handleUnifiedSearch = async (e) => {
    console.log('Search query is: ', searchQuery)
    const input = searchQuery.trim()
    if (!input) return;

    const isMobile = window.innerWidth <= 800; // or use your `isMobile` state
    const mode = searchMode; // 'location' or 'account'

    console.log("search initiated in mode:", mode)

    if (mode === 'location') {
      console.log("search by location", input)
      setGlobalSearchMode('location');
      await searchByZipCode(input); // or searchByZipCode(input) if you're only supporting zip
    } else if (mode === 'account') {
      console.log("search by account", input)
      const isAccountId = /^[0-9\-]+$/.test(input);
      if (isAccountId) {
        console.log("Isaccountid true")
        setGlobalSearchMode('accountid');

        await searchByAccountId(input);
      } else {
        setGlobalSearchMode('account');
        console.log("Isaccountid false")
        await searchByAccountName(input);
      }
    }

  };



  const sortedHistory = React.useMemo(() => {
    const ts = (x) => new Date(x?.timestamp || x?.createdAt || 0).getTime();
    return [...(searchHistory || [])].sort((a, b) => ts(b) - ts(a));
  }, [searchHistory]);


  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    console.log("Search for input: ", searchQuery);
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  const handleRecentLocationClick = (location) => {
    setSelectedLocation(location);
  };

  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value);
    setSearchRadius(newRadius);
  };

  const handleSearchHistoryClick = async (history) => {
    useMapStore.getState().replaySearch(history);
  };


  const handleFilterChange = (filterName) => {
    setFilters({
      ...filters,
      [filterName]: !filters[filterName]
    });
  };

  const getMarkerIcon = (status, business_unit) => {
    // If it's not US Broadline, it's considered Specialty — return early
    // console.log("map controls selected status: ", status)
    if (business_unit !== 'US Broadline' && status?.toLowerCase() !== 'target' && business_unit !== '') {
      return '/markers/specialty-marker.svg';
    }

    // For US Broadline, use switch on status
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '/markers/active-marker.svg';
      case 'ACTIVE_OLDER':
        return '/markers/active-older.svg';
      case 'ACTIVEOLDER':
        return '/markers/active-older.svg';
      case 'INACTIVE':
        return '/markers/active-older.svg';
      case 'PROSPECT':
        return '/markers/prospect-marker.svg';
      case 'TARGET':
        return '/markers/target-marker.svg';
      default:
        return '/markers/specialty-marker.svg';
    }
  };


  const hasActiveFilters = !filters.active || !filters.activeOlder || !filters.prospect || !filters.target;

  if (isMobile) {
    return (
      <div className="fixed top-0 left-0 right-0 z-mapCtrl pt-2 pb-0 pl-0 pr-0 pointer-events-none">
        <div className="flex items-center gap-2 mt-2 mb-2 px-2 pointer-events-auto w-full">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder={searchMode === 'location' ? 'Search by Address or Location' : 'Search by Account Name or ID'}
              className="search-field"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleUnifiedSearch(); }}
            />
          </div>

          {/* Toggle Buttons */}
          <div className="relative flex border border-blue-600 rounded-full bg-white shadow-sm text-fluid-sm font-medium overflow-hidden h-9">
            <div
              className={`absolute top-0 left-0 h-full w-1/2 bg-blue-600 rounded-full transition-transform duration-300 ${searchMode === 'account' ? 'translate-x-full' : ''
                }`}
            />
            <button onClick={() => setSearchMode('location')}
              className={`toggle-btn ${searchMode === 'location' ? 'text-white' : 'text-blue-600'}`} >
              <MapPinned className="w-4 h-4 mr-1" />
              Loc
            </button>
            <button onClick={() => setSearchMode('account')}
              className={`toggle-btn ${searchMode === 'account' ? 'text-white' : 'text-blue-600'}`} >
              <CircleUserRound className="w-4 h-4 mr-1" />
              Acct
            </button>
          </div>
        </div>


        {/* Filter Pills */}
        <div className="grid justify-center grid-cols-5 gap-[clamp(0.5px,1vw,5px)] mt-1 pb-1 px-2  pointer-events-auto whitespace-nonwrap overflow-hidden -mx-1">
          {/* Filter Pills */}
          <button
            className={`btn-pill ${filters.myAccountOnly ? 'pill-sky-sel' : 'pill-sky-unsel'}`}
            onClick={() => handleFilterChange('myAccountOnly')}
          >
            <span>My Accounts</span>
          </button>

          <button
            className={`btn-pill ${filters.active ? 'pill-green-sel' : 'pill-green-unsel'}`}
            onClick={() => handleFilterChange('active')}
          >
            Ship &lt; 52 W
          </button>

          <button
            className={`btn-pill ${filters.activeOlder ? 'pill-red-sel' : 'pill-red-unsel'}`}
            onClick={() => handleFilterChange('activeOlder')}
          >
            Ship &gt; 52 W
          </button>

          <button
            className={`btn-pill ${filters.prospect ? 'pill-blue-sel' : 'pill-blue-unsel'}`}
            onClick={() => handleFilterChange('prospect')}
          >
            Prospects
          </button>

          <button
            className={`btn-pill ${filters.target ? 'pill-purple-sel' : 'pill-purple-unsel'} truncate`}
            onClick={() => handleFilterChange('target')}
          >
            Targets
          </button>
        </div>


        {showSearchHereTag && mapCenter && (
          <div className="flex justify-center mt-3 pointer-events-auto w-fit mx-auto">
            <button
              onClick={() => {
                searchByLatLng(mapCenter.lat, mapCenter.lng);
                setGlobalSearchMode('Nearby Search')
                setShowSearchHereTag(false);
              }}
              className="bg-white px-3 py-1 border border-blue-100 rounded-full shadow text-fluid-sm font-medium text-blue-600 hover:bg-blue-50 transition"
            >
              Search this area
            </button>
          </div>
        )}

        {isOffFromDeviceLocation && deviceLocation && (
          <div className="flex justify-end mt-2 pointer-events-auto w-fit ml-auto">
            <button
              onClick={() => {
                attemptGeo(
                  pos => {
                    const fix = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
                    setDeviceLocation(fix);
                    setGlobalDeviceLocation(fix);
                    map?.panTo(fix);
                    map?.setZoom(15);
                    searchByLatLng(fix.lat, fix.lng);
                    setIsOffFromDeviceLocation(false);
                    setShowSearchHereTag(false);
                    setGlobalSearchMode('Nearby Search');
                  },
                  () => {
                    const fix = { ...defaultCenter, _fallback: true, ts: Date.now() };
                    // setDeviceLocation(fix); 
                    setGlobalDeviceLocation(fix);
                    map?.panTo(fix); map?.setZoom(15);
                    alert('Location unavailable – showing default area.');
                  }
                );
              }}
              className="inline-flex intems-center bg-white px-2 py-2 border border-gray-300 rounded-full shadow text-fluid-sm text-gray-700 hover:bg-gray-100 transition"
            >
              <LocateIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  }

  //Desktop map controls and filters
  return (
    <div className="relative">
      {/* Collapsible left panel */}
      <div className={`map-controls w-[400px] absolute top-0 left-0 flex flex-col overflow-visible bg-white shadow-md
         transition-transform duration-300 will-change-transform z-[5]
         ${showDesktopFilters ? 'h-screen' : ''}
         ${controlsOpen ? 'translate-x-0' : '-translate-x-[105%] pointer-events-none'}`
      }
      >



        <div className="p-4 border-b border-gray-200">
          {/* Toggle + Search Input for Web */}
          <div className="p-4 border-b border-gray-200 space-y-3">
            {/* Toggle */}
            <div className="flex justify-center">
              <div className="relative flex rounded-full border border-blue-500 bg-white shadow-sm text-fluid-sm font-medium overflow-hidden h-8 w-fit">
                <div className={`absolute top-0 left-0 h-full w-1/2 bg-blue-500 rounded-full transition-transform duration-300 ${searchMode === 'account' ? 'translate-x-full' : ''}`} />
                <button
                  onClick={() => setSearchMode('location')}
                  className={`relative z-10 w-24 text-fluid-sm transition-colors duration-200 ${searchMode === 'location' ? 'text-white' : 'text-blue-600'}`}
                >
                  Location
                </button>
                <button
                  onClick={() => setSearchMode('account')}
                  className={`relative z-10 w-24 text-fluid-sm transition-colors duration-200 ${searchMode === 'account' ? 'text-white' : 'text-blue-600'}`}
                >
                  Account
                </button>
              </div>
            </div>

            {/* Unified Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />

              <input
                type="text"
                name="search"
                placeholder={searchMode === 'location' ? 'Search by Address or ZIP Code' : 'Search by Account Name or ID'}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
              />

            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white z-10">
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            <span className="font-medium">Controls</span>
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-fluid-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-600 flex items-center"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </button>
            )}
            <div className="flex space-x-1">
              <button
                className={`px-2 py-1 text-fluid-xs rounded-md ${activeTab === 'filters' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('filters')}
              >
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-fluid-xs rounded-full">
                    {!filters.active + !filters.activeOlder + !filters.prospect + !filters.target}
                  </span>
                )}
              </button>
              <button
                className={`px-2 py-1 text-fluid-xs rounded-md ${activeTab === 'history' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('history')}
              >
                History
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'filters' ? (
          <div className="px-4 pb-4">
            {currentSearchTerm && (
              <div className="mt-4 mb-2 bg-blue-50 p-2 rounded-md">
                <div className="flex justify-between items-center">
                  <div className="text-fluid-sm font-medium text-blue-700">Current Search</div>
                </div>
                <div className="text-fluid-sm text-blue-600 flex items-center mt-1">
                  <Search className="h-3 w-3 mr-1" />
                  {currentSearchTerm}
                </div>
              </div>
            )}
            {/* Hidden Pills */}
            {hasActiveFilters && (

              <div className="mt-4 flex flex-wrap gap-2">

                {/* Ship < 52 Hidden */}
                {!filters.active && (
                  <span className="btn-pill pill-green-unsel flex items-center">
                    Ship &lt; 52 Weeks Hidden
                    <button onClick={() => handleFilterChange('active')} className="ml-1">
                      <X className="icon-sm" />
                    </button>
                  </span>
                )}

                {/* Ship > 52 Hidden */}
                {!filters.activeOlder && (
                  <span className="btn-pill pill-red-unsel flex items-center">
                    Ship &gt; 52 Weeks Hidden
                    <button onClick={() => handleFilterChange('activeOlder')} className="ml-1">
                      <X className="icon-sm" />
                    </button>
                  </span>
                )}

                {/* Prospects Hidden */}
                {!filters.prospect && (
                  <span className="btn-pill pill-blue-unsel flex items-center">
                    Prospects Hidden
                    <button onClick={() => handleFilterChange('prospect')} className="ml-1">
                      <X className="icon-sm" />
                    </button>
                  </span>
                )}

                {/* Targets Hidden */}
                {!filters.target && (
                  <span className="btn-pill pill-purple-unsel flex items-center">
                    Targets Hidden
                    <button onClick={() => handleFilterChange('target')} className="ml-1">
                      <X className="icon-sm" />
                    </button>
                  </span>
                )}

              </div>
            )}
            {/* Active pills */}
            <div className="flex flex-wrap gap-2 mt-4 mb-2">

              <div
                className={`btn-pill cursor-pointer ${filters.myAccountOnly ? 'pill-sky-sel' : 'pill-sky-unsel'}`}
                onClick={() => handleFilterChange('myAccountOnly')}
              >
                <div className="text-fluid-xs font-medium">My Account Only</div>
              </div>

              <div
                className={`btn-pill cursor-pointer ${filters.prospect ? 'pill-blue-sel' : 'pill-blue-unsel'}`}
                onClick={() => handleFilterChange('prospect')}
              >
                <div className="text-fluid-xs">Prospects</div>
              </div>

              <div
                className={`btn-pill cursor-pointer ${filters.target ? 'pill-purple-sel' : 'pill-purple-unsel'}`}
                onClick={() => handleFilterChange('target')}
              >
                <div className="text-fluid-xs">Targets</div>
              </div>

              <div
                className={`btn-pill cursor-pointer ${filters.active ? 'pill-green-sel' : 'pill-green-unsel'}`}
                onClick={() => handleFilterChange('active')}
              >
                <div className="text-fluid-xs">Ship &lt; 52 W</div>
              </div>

              <div
                className={`btn-pill cursor-pointer ${filters.activeOlder ? 'pill-red-sel' : 'pill-red-unsel'}`}
                onClick={() => handleFilterChange('activeOlder')}
              >
                <div className="text-fluid-xs">Ship &gt; 52 W</div>
              </div>

            </div>
            <div className="mb-4 border-t border-gray-200 pt-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleSection('distance')}
              >
                <h3 className="font-medium">Distance</h3>
                {expandedSections.distance ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>

              {expandedSections.distance && (
                <div className="mt-2">
                  <p className="text-fluid-sm mb-1">Search by {searchRadius} mi</p>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={searchRadius}
                    onChange={handleRadiusChange}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-fluid-xs mt-1">
                    <span>0 mi</span>
                    <span>50 mi</span>
                  </div>
                </div>
              )}
            </div>
            <section className="mb-4">
              <button
                className="w-full flex items-center justify-between py-2"
                onClick={() => setOpenSaved(v => !v)}
                aria-expanded={openSaved}
              >
                <h2 className="font-medium">Bookmarks</h2>
                {openSaved ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>

              {openSaved && (
                <div className="flex items-center justify-end pb-2 -mt-1">
                  <label className="flex items-center gap-2 text-fluid-sm">
                    <span className="text-gray-600">Show only</span>
                    <input
                      type="checkbox"
                      checked={!!showBookmarksOnly}
                      onChange={(e) => {
                        console.log('[MapControls] bookmarksOnly ->', e.target.checked);
                        setShowBookmarksOnly(e.target.checked);
                      }}
                      className="h-4 w-4 accent-blue-600"
                      aria-label="Show only bookmarked locations"
                    />
                  </label>
                </div>
              )}


              {openSaved && (
                <div className="space-y-3">
                  {favoriteCards.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">No saved accounts yet</div>
                  ) : (
                    favoriteCards.map((fav) => {
                      // minimal object for selection — use what your setSelectedLocation expects
                      const minimalLoc = {
                        id: fav.key,
                        name: fav.name,
                        address: fav.address,
                        lat: fav.lat,
                        lng: fav.lng,
                        status: fav.status,
                        scope: fav.accountType || fav.scope || '',
                        accountType: fav.accountType || fav.scope || '',
                      };
                      const favOn = isFavorite(minimalLoc);

                      return (
                        <div
                          key={fav.key}
                          className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm cursor-pointer hover:bg-gray-50"
                          onClick={() => setSelectedLocation(minimalLoc)}
                        >
                          <div className="flex items-center">
                            <div className="mr-3">
                              <img
                                src={getMarkerIcon(fav.status, '')}
                                alt="Location marker"
                                className="h-6 w-6"
                              />
                            </div>
                            <div>
                              <h3 className="font-semibold truncate ">{fav.name}</h3>
                              {fav.address && <p className="text-fluid-sm text-gray-500 truncate">{fav.address}</p>}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="p-2 -m-2 rounded-full hover:bg-gray-100 focus:outline-none"
                            aria-pressed={favOn}
                            aria-label={favOn ? 'Remove from favorites' : 'Save to favorites'}
                            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              toggleFavorite(minimalLoc);
                            }}
                          >
                            {favOn ? (
                              <BookmarkCheck className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Bookmark className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </section>

            {recentlyViewed.length > 0 && (
              <div className="mb-4 border-t border-gray-200 pt-4 ">
                <div
                  className="flex items-center justify-between cursor-pointer py-2"
                  onClick={() => toggleSection('recent')}
                >
                  <h3 className="font-medium">Recent</h3>
                  {expandedSections.recent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>

                {expandedSections.recent && (
                  <div className="mt-2 space-y-3">
                    {recentlyViewed.slice(0, 5).map((location) => (
                      <div
                        key={location.id}
                        className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm cursor-pointer hover:bg-gray-50"
                        onClick={() => handleRecentLocationClick(location)}
                      >
                        {/* Left: icon + text together */}
                        <div className="flex items-center">
                          <img
                            src={getMarkerIcon(
                              location.shipStatus || location.accountStatus,
                              location?.accountDetails?.businessUnit ?? ''
                            )}
                            alt="Location marker"
                            className="h-6 w-6 mr-3"
                          />
                          <div>
                            <p className="font-semibold truncate">{location.name}</p>
                            <p className="text-fluid-xs text-gray-500 truncate max-w-[180px]">
                              {location.address}
                            </p>
                          </div>
                        </div>

                        {/* Right: favorite toggle */}
                        <button
                          type="button"
                          className="p-2 -m-2 rounded-full hover:bg-gray-100 focus:outline-none"
                          aria-pressed={isFavorite(location)}
                          aria-label={
                            isFavorite(location)
                              ? 'Remove from favorites'
                              : 'Save to favorites'
                          }
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggleFavorite(location);
                          }}
                        >
                          {isFavorite(location) ? (
                            <BookmarkCheck className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Bookmark className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    ))}

                  </div>
                )}
              </div>
            )}

            {/* ---- Advanced Filters launcher ---- */}
            <div className="mb-4 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowDesktopFilters(true)}
                className="w-full flex items-center justify-between text-left"
              >
                <span className="font-medium">Advanced Filters</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {showDesktopFilters && (
              <div className="absolute inset-0 z-20 bg-white bottom-10">
                <Filters onClose={() => setShowDesktopFilters(false)} />
              </div>
            )}
          </div>

        ) : (

          <div className="px-4 pb-4">
            <div className="mt-4">
              <h3 className="font-medium mb-2 text-gray-700">Search History</h3>

              {searchHistory.length === 0 ? (
                <div className="text-center py-4">
                  <Clock className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-fluid-sm text-gray-500">No search history yet</p>
                  <p className="text-fluid-xs text-gray-400 mt-1">Your recent searches will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedHistory.map((history) => (
                    <div
                      key={history.id}
                      className="p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleSearchHistoryClick(history)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-2">
                            <Search className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium text-fluid-sm">{history.term}</p>
                            <p className="text-fluid-xs text-gray-500">
                              {new Date(history.timestamp).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-fluid-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                          {history.radius} mi
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* ── Desktop: floating GPS re‑center button ─────────────── */}


      </div>
      {!isMobile && (
        <button
          onClick={() => {
            const { toggleControls, controlsOpen } = useMapStore.getState();
            console.log('Navbar: toggle controls (was)', controlsOpen);
            toggleControls();
            console.log('After toggle: controlsOpen =', useMapStore.getState().controlsOpen);

          }}

          aria-pressed={useMapStore.getState().controlsOpen}
          className={`absolute ${controlsOpen ? 'right-[-430px]' : 'right-[-30px]'} top-[230px] z-[4]
                   h-10 w-10 bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors
                   flex items-center justify-center active:scale-[0.98]`}
          aria-label={controlsOpen ? 'Hide controls' : 'Show controls'}
          title={controlsOpen ? 'Hide controls' : 'Show controls'}
        >

          {controlsOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
      )}

    </div>
  );
};

export default MapControls;